import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d9spapt8o1vn02ekuji0';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();

  const article = articles.find((a) => a.id === articleId);
  const relatedArticles = articles
    .filter((a) => a.category === article?.category && a.id !== articleId)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer formUrl={NEWSLETTER_FORM_URL} />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: article.categoryLabel, to: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-600 rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {article.categoryLabel}
                </Link>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">· {article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">
                  {article.author.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                  <p className="text-xs text-foreground-400">亚洲在线特约作者</p>
                </div>
              </div>
            </header>

            <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 亚洲在线`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mb-10">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="border-t border-background-200 pt-8">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                  >
                    <div className="relative w-full aspect-[16/9] overflow-hidden">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-1">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.date,
            image: article.imageUrl,
          })}
        </script>
      </main>
      <Footer formUrl={NEWSLETTER_FORM_URL} />
    </>
  );
}